import { HttpError } from "./http-error";

function normalizeErrorMessage(value: unknown, fallback: string) {
  if (typeof value !== "string" || !value.trim()) {
    return fallback;
  }

  const trimmed = value.trim();
  if (/^</.test(trimmed) || /<html[\s>]/i.test(trimmed) || /<body[\s>]/i.test(trimmed)) {
    return "Booking service is temporarily unavailable. Please try again in a moment.";
  }

  return trimmed;
}

async function http<T>(path: string, config: RequestInit): Promise<T> {
  const request = new Request(path, config);
  const response: Response = await fetch(request);

  if (!response.ok) {
    const contentType = response.headers.get("content-type") || "";
    const fallbackMessage = response.statusText || `HTTP Error ${response.status}`;
    let errJson: { message?: string; data?: Record<string, unknown> } = {};

    if (contentType.includes("application/json")) {
      errJson = (await response.json().catch(() => ({}))) as typeof errJson;
    } else {
      const responseText = await response.text().catch(() => "");
      errJson = {
        message: normalizeErrorMessage(responseText, fallbackMessage),
      };
    }

    const err = HttpError.fromRequest(
      request,
      {
        ...response,
        statusText: normalizeErrorMessage(errJson.message, fallbackMessage),
      },
      errJson
    );
    throw err;
  }
  // may error if there is no body, return empty array
  return await response.json();
}

export async function get<T>(path: string, config?: RequestInit): Promise<T> {
  const init = { method: "GET", ...config };
  return await http<T>(path, init);
}

export async function post<T, U>(path: string, body: T, config?: RequestInit): Promise<U> {
  const init = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...config,
  };
  return await http<U>(path, init);
}

export async function put<T, U>(path: string, body: T, config?: RequestInit): Promise<U> {
  const init = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...config,
  };
  return await http<U>(path, init);
}

export async function patch<T, U>(path: string, body: T, config?: RequestInit): Promise<U> {
  const init = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...config,
  };
  return await http<U>(path, init);
}

export async function remove<T, U>(path: string, body: T, config?: RequestInit): Promise<U> {
  const init = {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...config,
  };
  return await http<U>(path, init);
}
